// First-touch landing-cluster attribution.
//
// Why this exists: v_seo_funnel.sql buckets sessions into landing_cluster
// (kundli/dosha/panchang/festival/muhurat/matching/rashifal/product/home/other)
// derived from page_path. But paid Razorpay orders all land in '_unattributed'
// because by the time the user reaches checkout (chat → paywall → Razorpay
// modal), the current path is /ai-astrologer/ or /, not the SEO page that
// drove discovery.
//
// Pattern: on first page load in a tab, derive the cluster and acquisition
// channel from the actual landing URL and persist a bounded, privacy-safe
// contract in sessionStorage. Subsequent payment-order creates send it to the
// private provider-create intent and audit log; provider notes stay opaque.

import { safeSessionGet, safeSessionRemove, safeSessionSet } from "@/lib/browser-storage";
import {
  ATTRIBUTION_POLICY_VERSION,
  classifyAcquisitionChannel,
  classifyLandingPath,
  isGovernedAcquisitionChannel,
  isGovernedLandingCluster,
  type AcquisitionChannel,
  type LandingCluster,
} from "@/lib/attribution-policy.generated";
import {
  REDIRECT_LANDING_COOKIE,
  normalizedLandingPath,
  shouldPersistLandingPath,
} from "@/lib/landing-attribution-policy";

const STORAGE_KEY = "ish_landing_cluster_v1";
const PATH_STORAGE_KEY = "ish_landing_path_v1";
const ATTRIBUTION_STORAGE_KEY = "ish_first_touch_attribution_v2";
const CAMPAIGN_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
] as const;
const FIRST_TOUCH_CAMPAIGN_FIELDS = [
  "first_touch_utm_source",
  "first_touch_utm_medium",
  "first_touch_utm_campaign",
  "first_touch_utm_content",
  "first_touch_utm_term",
  "first_touch_utm_id",
] as const;

export type { LandingCluster } from "@/lib/attribution-policy.generated";

export type FirstTouchAttribution = {
  landing_cluster: LandingCluster;
  landing_path: string;
  first_touch_channel: AcquisitionChannel;
  first_touch_referrer_host: string;
  first_touch_utm_source: string;
  first_touch_utm_medium: string;
  first_touch_utm_campaign: string;
  first_touch_utm_content: string;
  first_touch_utm_term: string;
  first_touch_utm_id: string;
  first_touch_has_ad_click_id: boolean;
  attribution_policy_version: number;
};

export function classifyPath(pathname: string): LandingCluster {
  return classifyLandingPath(pathname);
}

function boundedCampaignValue(value: string | null): string {
  return (value ?? "")
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .trim()
    .slice(0, 100);
}

function referrerHost(referrer: string): string {
  if (!referrer) return "";
  try {
    return new URL(referrer).hostname.toLowerCase().slice(0, 253);
  } catch {
    return "";
  }
}

function isBoundedCampaignValue(value: unknown): value is string {
  return typeof value === "string" && boundedCampaignValue(value) === value;
}

function isReferrerHost(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= 253 &&
    value === value.trim().toLowerCase() &&
    /^[a-z0-9.-]*$/.test(value)
  );
}

function isGovernedLandingPath(value: unknown, cluster: LandingCluster): value is string {
  return (
    typeof value === "string" &&
    value.length <= 512 &&
    normalizedLandingPath(value) === value &&
    shouldPersistLandingPath(value) &&
    classifyLandingPath(value) === cluster
  );
}

function hasValidFirstTouchCore(
  value: Partial<FirstTouchAttribution>
): value is FirstTouchAttribution {
  return (
    value.attribution_policy_version === ATTRIBUTION_POLICY_VERSION &&
    isGovernedLandingCluster(value.landing_cluster) &&
    isGovernedLandingPath(value.landing_path, value.landing_cluster) &&
    isGovernedAcquisitionChannel(value.first_touch_channel) &&
    isReferrerHost(value.first_touch_referrer_host) &&
    typeof value.first_touch_has_ad_click_id === "boolean"
  );
}

function hasValidFirstTouchCampaign(value: Partial<FirstTouchAttribution>): boolean {
  return FIRST_TOUCH_CAMPAIGN_FIELDS.every((field) => isBoundedCampaignValue(value[field]));
}

function buildFirstTouchAttribution(path: string, cluster: LandingCluster): FirstTouchAttribution {
  const location = typeof window !== "undefined" ? window.location.href : path;
  const referrer = typeof document !== "undefined" ? document.referrer : "";
  let search = new URLSearchParams();
  try {
    search = new URL(location, "https://ishvaram.com").searchParams;
  } catch {
    // A malformed browser URL must never block navigation or checkout.
  }
  const campaign = Object.fromEntries(
    CAMPAIGN_KEYS.map((key) => [key, boundedCampaignValue(search.get(key))])
  ) as Record<(typeof CAMPAIGN_KEYS)[number], string>;
  return {
    landing_cluster: cluster,
    landing_path: path,
    first_touch_channel: classifyAcquisitionChannel(referrer, location),
    first_touch_referrer_host: referrerHost(referrer),
    first_touch_utm_source: campaign.utm_source,
    first_touch_utm_medium: campaign.utm_medium,
    first_touch_utm_campaign: campaign.utm_campaign,
    first_touch_utm_content: campaign.utm_content,
    first_touch_utm_term: campaign.utm_term,
    first_touch_utm_id: campaign.utm_id,
    first_touch_has_ad_click_id: ["gclid", "gbraid", "wbraid"].some((key) => search.has(key)),
    attribution_policy_version: ATTRIBUTION_POLICY_VERSION,
  };
}

function readFirstTouchAttribution(): FirstTouchAttribution | null {
  const raw = safeSessionGet(ATTRIBUTION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<FirstTouchAttribution> | null;
    if (!parsed || typeof parsed !== "object") return null;
    if (!hasValidFirstTouchCore(parsed) || !hasValidFirstTouchCampaign(parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function readLegacyFirstTouch(): { cluster: LandingCluster; path: string } | null {
  const cluster = safeSessionGet(STORAGE_KEY);
  const path = safeSessionGet(PATH_STORAGE_KEY);
  if (!isGovernedLandingCluster(cluster) || !isGovernedLandingPath(path, cluster)) return null;
  return { cluster, path };
}

function redirectLandingCookie(): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${REDIRECT_LANDING_COOKIE}=`;
  const pair = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!pair) return null;
  try {
    return normalizedLandingPath(decodeURIComponent(pair.slice(prefix.length)));
  } catch {
    return null;
  }
}

function clearRedirectLandingCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${REDIRECT_LANDING_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}

/**
 * Persist the current pathname as the first-touch landing for this session
 * if not already set. Safe to call multiple times — only the first call wins.
 * Returns the persisted cluster (existing or just-stored).
 */
export function captureFirstTouchLanding(pathname?: string): LandingCluster {
  const existing = readLegacyFirstTouch();
  if (existing) {
    clearRedirectLandingCookie();
    return existing.cluster;
  }
  safeSessionRemove(STORAGE_KEY);
  safeSessionRemove(PATH_STORAGE_KEY);
  safeSessionRemove(ATTRIBUTION_STORAGE_KEY);

  const path = normalizedLandingPath(
    redirectLandingCookie() ??
      pathname ??
      (typeof window !== "undefined" ? window.location.pathname : "/")
  );
  const cluster = classifyPath(path);

  if (!shouldPersistLandingPath(path)) {
    clearRedirectLandingCookie();
    return cluster;
  }

  safeSessionSet(STORAGE_KEY, cluster);
  safeSessionSet(PATH_STORAGE_KEY, path);
  safeSessionSet(
    ATTRIBUTION_STORAGE_KEY,
    JSON.stringify(buildFirstTouchAttribution(path, cluster))
  );
  clearRedirectLandingCookie();
  return cluster;
}

/**
 * Read the persisted first-touch cluster without writing. Returns null if
 * the user landed before this helper started running (graceful degradation).
 */
export function getFirstTouchLanding(): {
  cluster: LandingCluster | null;
  path: string | null;
} {
  const firstTouch = readLegacyFirstTouch();
  return firstTouch ?? { cluster: null, path: null };
}

/** Read the full immutable first-touch contract. Legacy sessions intentionally
 * return null so channel proof is missing rather than reconstructed at checkout. */
export function getFirstTouchAttribution(): FirstTouchAttribution | null {
  return readFirstTouchAttribution();
}
